import { useState } from "react";
import styled from "styled-components";
import { apiGet } from "@services/newApi/typed";
import { ModalComponent } from "../../../components/ui/Modal";
import LoadingSpinner from "../../../assets/icons/LoadingSpinner";
import loggedUserTitle from "../../../components/feature/Aside/utils";
import { theme } from "../../../styles/theme";
import { useSwitchUser } from "../hooks/useSwitchUser";
import api from "@services/newApi/api";
import { toast } from "react-toastify";
import { ROLE_DOCTOR } from "@/types/role";
import { USER_STATUS_PENDING_APPROVAL } from "@/types/user";
import ApproveDoctor from "../../../pages/Users/ApproveDoctor/ApproveDoctor";

interface UserRow {
  id: number;
  name: string;
  email?: string;
  role_id?: number;
  hospital_name?: string;
  status?: number;
  crm?: string;
}

/**
 * Picker "Ver como outro usuário" — busca usuários por nome e entra na sessão
 * do escolhido via `useSwitchUser` (endpoint admin `/auth/switch-user`).
 * Reversível pelo banner de personificação (switch-back).
 */
export function SwitchUserModal({ onClose }: { onClose: () => void }) {
  const [term, setTerm] = useState("");
  const [rows, setRows] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const { switchUser, isLoading: switching } = useSwitchUser();
  const [pendingSimple, setPendingSimple] = useState<UserRow | null>(null);
  const [pendingDoctor, setPendingDoctor] = useState<UserRow | null>(null);
  const [approving, setApproving] = useState(false);
  const busy = switching || approving;

  // Personificar um usuário "aguardando aprovação" (status 3): médico → modal
  // ApproveDoctor (conferência CRM×Tasy); demais → confirmação simples. Em
  // AMBOS a aprovação leva a status 2 (aguardando cadastro de senha — médico
  // recebe link de reset, ver adr doctor-register-local-not-ords), então NÃO
  // entra: aprova + avisa. Status ativo/normal entra direto.
  async function handleVerComo(u: UserRow) {
    if (u.status === USER_STATUS_PENDING_APPROVAL) {
      if (u.role_id === ROLE_DOCTOR) setPendingDoctor(u);
      else setPendingSimple(u);
      return;
    }
    // Sem reload: a navegação agora é client-side, então fecho o modal ao entrar
    // na sessão personificada (antes o window.location.href desmontava tudo).
    const res = await switchUser(u.id);
    if (res.success) onClose();
  }

  async function approveSimple(u: UserRow) {
    setApproving(true);
    try {
      // Não-médico: rota de aprovação/ativação (status 3 → status 2 + e-mail de
      // cadastro de senha). NÃO entra — o acesso só é liberado após a senha.
      // (A rota de médico exige hash/cod_doctor; ver bug de rota do hook
      // compartilhado useApproveUserMutation, fora do escopo desta correção.)
      await api.post(`/users/hospital-users/${u.id}/approval`, { active: true });
      toast.success(
        "Usuário aprovado. Enviamos um e-mail para cadastro de senha.",
      );
      setPendingSimple(null);
      onClose();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Erro ao aprovar usuário");
    } finally {
      setApproving(false);
    }
  }

  async function search() {
    const q = term.trim();
    if (!q) return;
    setLoading(true);
    try {
      const data = await apiGet<{ users: UserRow[] }>("/users", {
        params: { name: q, page: 1, perPage: 8 },
      });
      setRows(data.users ?? []);
    } catch {
      setRows([]);
    } finally {
      setSearched(true);
      setLoading(false);
    }
  }

  // Médico pendente: reusa o modal completo de aprovação (CRM×Tasy). É um Overlay
  // próprio; ao aprovar (onApproved) entra na personificação.
  if (pendingDoctor) {
    return (
      <ApproveDoctor
        id={pendingDoctor.id}
        name={pendingDoctor.name}
        email={pendingDoctor.email ?? ""}
        crm={pendingDoctor.crm ?? ""}
        setOpenAprove={() => setPendingDoctor(null)}
        onApproved={() => {
          // Aprovado = status 2 (médico ainda vai cadastrar a senha via link de
          // reset) → não dá pra personificar numa sessão usável; só avisa e fecha.
          toast.info(
            "Médico aprovado. Ele receberá um e-mail para cadastrar a senha antes de acessar.",
          );
          setPendingDoctor(null);
          onClose();
        }}
      />
    );
  }

  return (
    <ModalComponent
      closeModal={onClose}
      isWhite={false}
      label={pendingSimple ? "Aprovar usuário" : "Ver como outro usuário"}
    >
      {/* O role/aria-modal saiu daqui: quem os declara é o ModalBox, dono do
          diálogo (G32). Mantê-los no Card criava diálogo dentro de diálogo. */}
      <Card>
        {pendingSimple ? (
          <>
            <Header>
              <Title>Aprovar usuário</Title>
              <Sub>
                <strong>{pendingSimple.name}</strong> está aguardando aprovação.
                Ao aprovar, enviaremos um e-mail para ele cadastrar a senha; o
                acesso é liberado depois disso.
              </Sub>
            </Header>
            <SearchRow>
              <VerBtn
                type="button"
                disabled={busy}
                onClick={() => setPendingSimple(null)}
              >
                Cancelar
              </VerBtn>
              <SearchBtn
                type="button"
                disabled={busy}
                onClick={() => approveSimple(pendingSimple)}
              >
                {busy ? (
                  <LoadingSpinner size={18} strokeColor="#fff" />
                ) : (
                  "Aprovar"
                )}
              </SearchBtn>
            </SearchRow>
          </>
        ) : (
          <>
            <Header>
              <Title>Ver como outro usuário</Title>
              <Sub>
                Busque um usuário e entre na sessão dele para visualizar o portal
                como ele. Você pode voltar à sua sessão a qualquer momento.
              </Sub>
            </Header>

            <SearchRow>
              <Input
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") search();
                }}
                placeholder="Nome do usuário"
                autoFocus
              />
              <SearchBtn type="button" onClick={search} disabled={loading}>
                {loading ? (
                  <LoadingSpinner size={18} strokeColor="#fff" />
                ) : (
                  "Buscar"
                )}
              </SearchBtn>
            </SearchRow>

            <List>
              {loading ? (
                <Empty>Buscando…</Empty>
              ) : rows.length > 0 ? (
                rows.map((u) => (
                  <Item key={u.id}>
                    <div style={{ minWidth: 0 }}>
                      <Name>{u.name}</Name>
                      <Meta>
                        {loggedUserTitle(u.role_id ?? 0)}
                        {u.email ? ` · ${u.email}` : ""}
                      </Meta>
                    </div>
                    <VerBtn
                      type="button"
                      disabled={busy}
                      onClick={() => handleVerComo(u)}
                    >
                      Ver como
                    </VerBtn>
                  </Item>
                ))
              ) : (
                <Empty>
                  {searched
                    ? "Nenhum usuário encontrado."
                    : "Digite um nome e clique em Buscar."}
                </Empty>
              )}
            </List>
          </>
        )}
      </Card>
    </ModalComponent>
  );
}

const Card = styled.div`
  width: 520px;
  max-width: 90vw;
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  box-sizing: border-box;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: ${theme.colors.fg1};
`;

const Sub = styled.p`
  margin: 0;
  font-size: 15px;
  color: ${theme.colors.fg2};
`;

const SearchRow = styled.div`
  display: flex;
  gap: 12px;
`;

const Input = styled.input`
  flex: 1;
  height: 48px;
  padding: 0 16px;
  border: 1.5px solid ${theme.colors.borderStrong};
  border-radius: ${theme.radii.sm};
  background: ${theme.colors.surface};
  font-family: inherit;
  font-size: 16px;
  color: ${theme.colors.fg1};
  outline: none;

  &:focus {
    border-color: ${theme.colors.primary};
  }
`;

const SearchBtn = styled.button`
  min-width: 110px;
  height: 48px;
  padding: 0 20px;
  border: none;
  border-radius: 10px;
  background: ${theme.colors.primary};
  color: #fff;
  font-family: inherit;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 340px;
  overflow-y: auto;
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.sm};
`;

const Name = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${theme.colors.fg1};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Meta = styled.div`
  font-size: 13px;
  color: ${theme.colors.fg3};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const VerBtn = styled.button`
  flex: none;
  height: 38px;
  padding: 0 16px;
  border: 1.5px solid ${theme.colors.borderStrong};
  border-radius: ${theme.radii.sm};
  background: ${theme.colors.surface};
  color: ${theme.colors.fg1};
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${theme.colors.gray50};
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Empty = styled.div`
  padding: 24px 8px;
  text-align: center;
  font-size: 14px;
  color: ${theme.colors.fg3};
`;
